<script setup>
  import { storeToRefs } from "pinia";
  import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
  import { FormItem } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import TargetConditions from "./TargetConditions.vue";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
  import { useCurrentUserStore } from "@stores/currentUser";

  const surveyTargetsStore = useSurveyTargetsStore();
  const currentUserStore = useCurrentUserStore();

  const { isAdmin } = storeToRefs(currentUserStore);
  const { selectedTarget, selectedTargetIndex, countryOptions, selectedTargetValidationErrors } =
    storeToRefs(surveyTargetsStore);
  const { updateTargetCountries } = surveyTargetsStore;

  const errorFor = (name) => selectedTargetValidationErrors.value.find((e) => e.name === name)?.message;
</script>

<template>
  <Card class="p-6">
    <CardHeader class="pb-6">
      <CardTitle class="heading-page">Target settings</CardTitle>
    </CardHeader>
    <CardContent class="space-y-6">
      <Badge
        v-if="errorFor('duplicate')"
        variant="destructive"
        class="text-xs">
        {{ errorFor("duplicate") }}
      </Badge>

      <div class="grid grid-cols-2 gap-x-4 gap-y-2 items-start">
        <FormItem>
          <Label>Name <span class="text-destructive">*</span></Label>
          <Input v-model="selectedTarget.name" />
          <Badge
            v-if="errorFor('name')"
            variant="destructive"
            class="text-xs mt-1">
            {{ errorFor("name") }}
          </Badge>
        </FormItem>

        <FormItem>
          <Label>Countries <span class="text-destructive">*</span></Label>
          <Select
            :model-value="selectedTarget.countries"
            :options="countryOptions"
            multiple
            filterable
            placeholder="Select countries..."
            @update:model-value="updateTargetCountries(selectedTargetIndex, $event)" />
          <Badge
            v-if="errorFor('countries')"
            variant="destructive"
            class="text-xs mt-1">
            {{ errorFor("countries") }}
          </Badge>
        </FormItem>

        <FormItem>
          <Label>Target (N) <span class="text-destructive">*</span></Label>
          <Input
            type="number"
            :model-value="selectedTarget.target ?? ''"
            @update:model-value="(val) => (selectedTarget.target = val === '' ? null : Number(val))" />
          <Badge
            v-if="errorFor('target')"
            variant="destructive"
            class="text-xs mt-1">
            {{ errorFor("target") }}
          </Badge>
        </FormItem>

        <FormItem>
          <Label>CPI</Label>
          <Input
            type="number"
            :model-value="selectedTarget.cpi ?? ''"
            @update:model-value="(val) => (selectedTarget.cpi = val === '' ? null : Number(val))" />
        </FormItem>

        <FormItem v-if="isAdmin">
          <Label>CRM ID</Label>
          <Input v-model="selectedTarget.crm_id" />
        </FormItem>
      </div>

      <TargetConditions />
    </CardContent>
  </Card>
</template>
